import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Mail, Phone } from "lucide-react";
import { toast } from "sonner";

const DEFAULT_NS = {
  whatsapp_enabled: true,
  email_enabled: false,
  before_shift_enabled: true,
  before_shift_minutes: 5,
  missing_clockin_enabled: true,
  missing_clockin_minutes: 10,
  language: "pt",
};

export function initMemberForm(member) {
  const contact = member.contact || {
    email: member.email || "",
    phone: member.phone || "",
    whatsapp: member.phone || "",
    country_code: "+351",
  };
  return {
    name: member.name || "",
    role: member.role || "",
    active: member.active !== false,
    contact: { ...contact },
    notification_settings: { ...DEFAULT_NS, ...(member.notification_settings || {}) },
  };
}

function SwitchRow({ id, label, checked, onChange, children }) {
  return (
    <div>
      <div className="flex items-center justify-between">
        <Label htmlFor={id} className="text-sm cursor-pointer">{label}</Label>
        <Switch id={id} checked={checked} onCheckedChange={onChange} />
      </div>
      {children}
    </div>
  );
}

function MessagePreview({ ns }) {
  const examples = {
    pt: {
      before: "⏰ Olá [Nome]! O seu turno começa às 20:00. Não se esqueça de registar a entrada.",
      missing: "⚠️ Olá [Nome]! Ainda não registaste a entrada do turno das 20:00. Por favor, regista agora.",
    },
    es: {
      before: "⏰ ¡Hola [Nombre]! Tu turno comienza a las 20:00. No olvides fichar la entrada.",
      missing: "⚠️ ¡Hola [Nombre]! Aún no has fichado la entrada del turno de las 20:00.",
    },
    en: {
      before: "⏰ Hi [Name]! Your shift starts at 20:00. Don't forget to clock in.",
      missing: "⚠️ Hi [Name]! You haven't clocked in for your 20:00 shift yet.",
    },
    ar: {
      before: "⏰ مرحباً [الاسم]! يبدأ وردتك في الساعة 20:00. لا تنسَ تسجيل حضورك.",
      missing: "⚠️ مرحباً [الاسم]! لم تسجّل حضورك لوردة الساعة 20:00 بعد.",
    },
  };
  const ex = examples[ns.language] || examples.pt;
  const lines = [];
  if (ns.before_shift_enabled) lines.push({ label: `${ns.before_shift_minutes} min antes`, text: ex.before });
  if (ns.missing_clockin_enabled) lines.push({ label: `${ns.missing_clockin_minutes} min depois (sem ponto)`, text: ex.missing });
  if (!lines.length) return null;
  return (
    <div className="rounded-lg border border-dashed border-blue-300 bg-blue-50/50 p-3 space-y-2">
      <p className="text-xs font-medium text-blue-700">Pré-visualização das mensagens</p>
      {lines.map((l, i) => (
        <div key={i}>
          <p className="text-[10px] text-blue-500 mb-0.5">{l.label}</p>
          <p className="text-xs text-blue-900 leading-relaxed">{l.text}</p>
        </div>
      ))}
    </div>
  );
}

export function MemberEditModal({ member, onClose, onSaved }) {
  const { authAxios } = useAuth();
  const [form, setForm] = useState(() => initMemberForm(member));
  const [saving, setSaving] = useState(false);

  const setField = (path, value) => {
    setForm(prev => {
      const next = { ...prev };
      const keys = path.split(".");
      let cur = next;
      for (let i = 0; i < keys.length - 1; i++) {
        cur[keys[i]] = { ...cur[keys[i]] };
        cur = cur[keys[i]];
      }
      cur[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await authAxios.patch(`/timeclock/members/${member.id}`, form);
      toast.success("Funcionário atualizado");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao guardar");
    } finally {
      setSaving(false);
    }
  };

  const ns = form.notification_settings;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold text-sm shrink-0">
              {member.name?.charAt(0)?.toUpperCase()}
            </div>
            Editar Funcionário
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 pt-1">
          {/* Basic info */}
          <section className="space-y-3">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Dados Gerais</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-xs mb-1 block">Nome</Label>
                <Input value={form.name} onChange={e => setField("name", e.target.value)} />
              </div>
              <div>
                <Label className="text-xs mb-1 block">Cargo / Função</Label>
                <Input value={form.role} onChange={e => setField("role", e.target.value)} placeholder="ex: Chef, Garçon" />
              </div>
              <div className="flex items-center gap-2 pt-5">
                <Switch checked={form.active} onCheckedChange={v => setField("active", v)} id="sw-active" />
                <Label htmlFor="sw-active" className="text-sm cursor-pointer">Ativo</Label>
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="space-y-3">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
              <Phone className="w-3.5 h-3.5" /> Contacto
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs mb-1 block">Código do País</Label>
                <Input value={form.contact.country_code} onChange={e => setField("contact.country_code", e.target.value)} placeholder="+351" className="font-mono" />
              </div>
              <div>
                <Label className="text-xs mb-1 block">Telefone</Label>
                <Input
                  value={form.contact.phone}
                  onChange={e => {
                    setField("contact.phone", e.target.value);
                    if (form.contact.whatsapp === form.contact.phone || !form.contact.whatsapp) {
                      setField("contact.whatsapp", e.target.value);
                    }
                  }}
                  placeholder="912345678"
                  className="font-mono"
                />
              </div>
              <div>
                <Label className="text-xs mb-1 block">WhatsApp</Label>
                <Input value={form.contact.whatsapp} onChange={e => setField("contact.whatsapp", e.target.value)} placeholder="912345678" className="font-mono" />
              </div>
              <div>
                <Label className="text-xs mb-1 block flex items-center gap-1"><Mail className="w-3 h-3" />E-mail</Label>
                <Input type="email" value={form.contact.email} onChange={e => setField("contact.email", e.target.value)} placeholder="nome@exemplo.com" />
              </div>
            </div>
          </section>

          {/* Notification settings */}
          <section className="space-y-3">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Notificações de Ponto</h3>
            <div className="space-y-2.5 rounded-lg border border-border/60 p-3 bg-muted/30">
              <SwitchRow id="sw-wa" label="Avisar por WhatsApp" checked={ns.whatsapp_enabled} onChange={v => setField("notification_settings.whatsapp_enabled", v)} />
              <SwitchRow id="sw-email" label="Avisar por E-mail" checked={ns.email_enabled} onChange={v => setField("notification_settings.email_enabled", v)} />
              <div className="border-t border-border/40 my-1" />
              <SwitchRow id="sw-before" label="Lembrete antes do turno" checked={ns.before_shift_enabled} onChange={v => setField("notification_settings.before_shift_enabled", v)}>
                {ns.before_shift_enabled && (
                  <div className="flex items-center gap-1.5 mt-1.5 ml-8">
                    <Input type="number" min={1} max={60} value={ns.before_shift_minutes}
                      onChange={e => setField("notification_settings.before_shift_minutes", parseInt(e.target.value, 10) || 5)}
                      className="h-7 w-16 text-center text-sm font-mono" />
                    <span className="text-xs text-muted-foreground">minutos antes</span>
                  </div>
                )}
              </SwitchRow>
              <SwitchRow id="sw-missing" label="Alertar se não bater ponto" checked={ns.missing_clockin_enabled} onChange={v => setField("notification_settings.missing_clockin_enabled", v)}>
                {ns.missing_clockin_enabled && (
                  <div className="flex items-center gap-1.5 mt-1.5 ml-8">
                    <Input type="number" min={1} max={120} value={ns.missing_clockin_minutes}
                      onChange={e => setField("notification_settings.missing_clockin_minutes", parseInt(e.target.value, 10) || 10)}
                      className="h-7 w-16 text-center text-sm font-mono" />
                    <span className="text-xs text-muted-foreground">min após início sem entrada</span>
                  </div>
                )}
              </SwitchRow>
              <div className="border-t border-border/40 my-1" />
              <div className="flex items-center justify-between">
                <Label className="text-sm">Idioma da mensagem</Label>
                <Select value={ns.language} onValueChange={v => setField("notification_settings.language", v)}>
                  <SelectTrigger className="h-8 w-28 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pt">🇵🇹 PT</SelectItem>
                    <SelectItem value="es">🇪🇸 ES</SelectItem>
                    <SelectItem value="en">🇬🇧 EN</SelectItem>
                    <SelectItem value="ar">🇸🇦 AR</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <MessagePreview ns={ns} />
          </section>

          <div className="flex gap-2 pt-1">
            <Button variant="outline" className="flex-1" onClick={onClose}>Cancelar</Button>
            <Button className="flex-1" onClick={save} disabled={saving}>{saving ? "A guardar…" : "Guardar"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
